// The choices made on the server-creation page, in one place.
//
// A server can be created down three different paths (free, Stripe checkout, PayPal
// subscription) and the paid ones round-trip through a payment provider's metadata before
// the server actually gets built. Normalising and validating here keeps all three in
// agreement instead of each re-deriving defaults.

import { getVersionsForType, PROXIED_JAR_TYPES } from './jarCatalog';
import { fabricVersionInfo, neoforgeVersionInfo } from './jarVersions';

// Loaders offered at creation time: the same lineup the in-panel version switcher gives a
// proxied (free-tier) server. A new server is always proxied, so Spigot/Vanilla, which
// cannot join the proxy, are never offered.
export const CREATE_JAR_TYPES = PROXIED_JAR_TYPES;

export const DEFAULT_JAR_TYPE = 'paper';

/** Which plugin/mod ecosystem a loader belongs to. */
export function jarTypeFamily(jarType) {
  const id = String(jarType || '').toLowerCase();
  if (id === 'fabric') return 'fabric';
  if (id === 'neoforge') return 'neoforge';
  return 'bukkit';
}

/** Loaders that take mods rather than plugins, so the panel shows a Mods tab. */
export function isModLoader(jarType) {
  return jarTypeFamily(jarType) !== 'bukkit';
}

/**
 * The bundled template a loader provisions from. Paper, Purpur and Leaf all share the
 * `leaf` template's Bukkit configs (paper-global.yml, voicechat defaults); Fabric uses the
 * `fabric` template's FabricProxy-Lite config; NeoForge has its own. The template only
 * supplies config files - which jar gets installed comes from jarType, and which extras get
 * installed comes from the install flags below.
 */
export function templateForJarType(jarType) {
  const family = jarTypeFamily(jarType);
  if (family === 'fabric') return 'fabric';
  if (family === 'neoforge') return 'neoforge';
  return 'leaf';
}

/**
 * Whether the "preinstall ViaVersion" option can do anything for this loader.
 *
 * NeoForge has no server-side Via implementation - ViaForge and ViaNeoForgePlus are both
 * client-side only - so for those servers multi-version support comes from ViaVersion on
 * the Velocity proxy instead, and the option is not offered.
 */
export function supportsViaVersion(jarType) {
  return jarTypeFamily(jarType) !== 'neoforge';
}

/**
 * The Java runtime a loader+version needs, or null to let the container decide.
 *
 * This has to be pinned at creation, not just when the version is changed later. Left to
 * pick for itself the container installs the newest JRE it can find, and NeoForge will not
 * start on it: Java 27's JRE dropped the `jdk.crypto.ec` module that Minecraft's auth stack
 * still requires, so the server dies with "Module jdk.crypto.ec not found".
 */
export function javaForCreateOptions(jarType, mcVersion) {
  const family = jarTypeFamily(jarType);
  if (family === 'neoforge') return neoforgeVersionInfo(mcVersion)?.java ?? null;
  if (family === 'fabric') return fabricVersionInfo(mcVersion)?.java ?? null;
  return null;
}

function asBool(value, fallback) {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  return String(value).toLowerCase() === 'true' || String(value) === '1';
}

/**
 * Reads the creation options out of a request body or a payment provider's metadata.
 *
 * Defaults keep a server behaving the way servers behaved before these options existed:
 * Bedrock allowed, and ViaVersion/SimpleVoiceChat preinstalled (both used to be baked into
 * the Bukkit template unconditionally).
 */
export function normalizeCreateOptions(input = {}) {
  const raw = String(input.jarType ?? input.jar_type ?? '').toLowerCase();
  const jarType = CREATE_JAR_TYPES.includes(raw) ? raw : DEFAULT_JAR_TYPE;

  const rawVersion = String(input.mcVersion ?? input.mc_version ?? '').trim();

  return {
    jarType,
    // Empty means "let the container pick the newest release for this loader".
    mcVersion: rawVersion,
    bedrockEnabled: asBool(input.bedrockEnabled ?? input.bedrock_enabled, true),
    // Off unless explicitly turned on, including for anything created before the option existed.
    crackedEnabled: asBool(input.crackedEnabled ?? input.cracked_enabled, false),
    installViaVersion: asBool(input.installViaVersion ?? input.install_viaversion, true),
    installVoiceChat: asBool(input.installVoiceChat ?? input.install_voicechat, true),
  };
}

/** Flattens options into the string map a payment provider's metadata can carry. */
export function createOptionsToMetadata(options) {
  return {
    jar_type: options.jarType,
    mc_version: options.mcVersion || '',
    bedrock_enabled: options.bedrockEnabled ? 'true' : 'false',
    cracked_enabled: options.crackedEnabled ? 'true' : 'false',
    install_viaversion: options.installViaVersion ? 'true' : 'false',
    install_voicechat: options.installVoiceChat ? 'true' : 'false',
  };
}

/**
 * Checks the requested Minecraft version is one this loader actually offers, so a server is
 * never provisioned on a version its jar does not exist for.
 *
 * Returns the version to use: the requested one when valid, or '' to let the container pick
 * the newest. Throws only when the version was explicitly requested and is not available.
 */
export async function resolveCreateVersion(jarType, mcVersion) {
  if (!mcVersion) return '';

  const versions = await getVersionsForType(jarType);
  // An upstream outage leaves the list empty; trust the client's choice rather than
  // blocking creation outright, since the container resolves the jar itself anyway.
  if (versions.length === 0) return mcVersion;

  if (!versions.some((entry) => entry.id === mcVersion)) {
    throw new Error(`Minecraft ${mcVersion} is not available for ${jarType}.`);
  }
  return mcVersion;
}
